import TimeConstants from '@module/utils/item';
import type { MediaPlayer } from 'amazon-ivs-player';
import type Player from 'video.js/dist/types/player';
import { create, type StateCreator } from 'zustand';
import { plyrStateSafeLocalStorage } from '../lib/storage/storageImplementations';

interface PlayerState {
  player: Player | null;
  setPlayer: (player: Player | null) => void;
  ivsPlayer: MediaPlayer | null;
  setIvsPlayer: (ivsPlayer: MediaPlayer | null) => void;
  isPlayerFullscreen: boolean;
  volume: number;
  setVolume: (volume: number) => void;
  volumeBefore: number;
  setVolumeBefore: (volume: number) => void;
  isMute: boolean;
  setIsMute: (isMute: boolean) => void;
  togglePlayerFullscreen: () => void;
  setPlayerFullscreen: (val: boolean) => void;
  isPipMode: boolean;
  setIsPipMode: (val: boolean | ((prev: boolean) => boolean)) => void;
  isPlayerToolbarVisible: boolean;
  setIsPlayerToolbarVisible: (val: boolean | ((prev: boolean) => boolean)) => void;
  dispose: () => void;
}

const store: StateCreator<PlayerState, [], [], PlayerState> = (set, get) => {
  let toolbarTimeout: NodeJS.Timeout | null = null;
  return {
    player: null,
    setPlayer: (player: Player | null) => set((state) => ({ ...state, player })),
    ivsPlayer: null,
    setIvsPlayer: (ivsPlayer: MediaPlayer | null) => set((state) => ({ ...state, ivsPlayer })),
    isPlayerFullscreen: false,
    volume: plyrStateSafeLocalStorage.get()?.volume ?? 0.5,
    setVolume: (volume: number) => set((state) => ({ ...state, volume })),
    volumeBefore: plyrStateSafeLocalStorage.get()?.volume ?? 0.5,
    setVolumeBefore: (volume: number) => set((state) => ({ ...state, volumeBefore: volume })),
    isMute: false,
    setIsMute: (isMute: boolean) => set((state) => ({ ...state, isMute })),
    togglePlayerFullscreen: () => set((state) => ({ ...state, isPlayerFullscreen: !state.isPlayerFullscreen })),
    setPlayerFullscreen: (val) => set((state) => ({ ...state, isPlayerFullscreen: val })),
    isPipMode: false,
    setIsPipMode: (val) =>
      set((state) => ({
        isPipMode: typeof val === 'function' ? val(state.isPipMode) : val,
      })),
    isPlayerToolbarVisible: false,
    setIsPlayerToolbarVisible: (val) => {
      if (toolbarTimeout) {
        clearTimeout(toolbarTimeout);
        toolbarTimeout = null;
      }
      const next = typeof val === 'function' ? val(get().isPlayerToolbarVisible ?? false) : val;

      set((state) => ({
        ...state,
        isPlayerToolbarVisible: next,
      }));

      if (next) {
        toolbarTimeout = setTimeout(() => {
          set((state) => ({
            ...state,
            isPlayerToolbarVisible: false,
          }));
        }, TimeConstants.ONE_SECOND * 3);
      }
    },
    dispose: () => {
      const player = get().player;
      const ivsPlayer = get().ivsPlayer;
      if (player) {
        player.dispose();
      }
      if (ivsPlayer) {
        ivsPlayer.delete();
      }
      set((state) => ({
        ...state,
        player: null,
        ivsPlayer: null,
      }));
    },
  };
};

const useOldPlayerStore = create<PlayerState>()(store);

export default useOldPlayerStore;
